import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Nullable, floatVal } from "@jamesgmarks/utilities";
import { appendRange, sortBy } from "@hydra/internal";
import isEqual from "lodash/isEqual";

import { ClientDropdown } from "src/components/parts/ClientDropdown";
import { IClientDropdownItem } from "src/entity-interfaces/IClientDropdownItem";
import { AutoDisableButton } from "src/components/parts/AutoDisableButton";
import {
  IVBPData,
  IVariableBasePriceOverrideWithRange,
  VariableBasePriceEditor,
} from "src/components/pages/Subscriptions/EditorComponents/VariableBasePriceEditor/VariableBasePriceEditor";
import {
  VariableBasePriceTable,
} from "src/components/pages/Subscriptions/EditorComponents/VariableBasePriceEditor/VariableBasePriceTable";
import {
  IPromotedListingContract,
  IPromotedListingContractPriceUpdate,
  IPromotedListingContractPriceUpdateResult,
  IPromotedListingPriceBracket,
} from "src/entity-interfaces/IPromotedListingContractPricing";
import {
  clearPromotedListingContracts,
  loadPromotedListingContracts,
  savePromotedListingContractPrices,
} from "src/redux/features/promotedListings/actions";
import { useAppSelector } from "src/redux/hooks";
import { ELoadState } from "src/redux/utils";

/** An empty editor payload, used before a contract has been picked. */
const EMPTY_VBP_DATA: IVBPData = { rangedVBP: [], rangedVBPO: [] };

/**
 * Adds the human-readable `1-10` / `11+` range label to a sorted bracket list.
 *
 * `appendRange` throws on a bracket set it cannot label — more than one open-ended bracket, or one
 * that is not last — which is data this tool exists to repair, so a throw is reported rather than
 * left to break the page.
 */
const toRangedBrackets = (brackets: IPromotedListingPriceBracket[]) => {
  try {
    return { rows: appendRange(sortBy(brackets, 'maxValue'), 'maxValue'), error: null };
  } catch (error) {
    return {
      rows: [],
      error: error instanceof Error ? error.message : 'These price brackets could not be read.',
    };
  }
};

/**
 * What the operator has set up in the editor but not yet reviewed — the part of an update that the
 * brackets themselves determine. The note and the dry-run flag are added when the request is made,
 * so editing the note after staging a change still takes effect.
 */
type TStagedPriceChange = Pick<IPromotedListingContractPriceUpdate, 'useDefaultPrices' | 'brackets'>;

/**
 * Normalises bracket rows — from the editor or from the loaded contract — into the payload shape.
 *
 * Both sides of the "has anything actually changed?" comparison go through this, so stored DECIMAL
 * values and freshly typed ones end up as the same numbers rather than comparing unequal.
 */
const toStagedBrackets = (
  rows: (IPromotedListingPriceBracket | IVariableBasePriceOverrideWithRange)[],
): TStagedPriceChange['brackets'] => (
  rows.map((row) => ({
    maxValue: row.maxValue ?? null,
    basePrice: floatVal(`${row.basePrice ?? 0}`),
    costPerUnit: floatVal(`${row.costPerUnit ?? 0}`),
  }))
);

/** Describes a contract in the picker: plan, subscription id, and whether it is priced off-plan. */
const buildContractLabel = (contract: IPromotedListingContract) => {
  const pricingLabel = contract.variableBasePriceOverrides.length > 0 ? 'custom prices' : 'plan prices';
  const statusLabel = contract.isActive ? '' : ' — expired';

  return `${contract.subscriptionId} — ${contract.planLabel} (${pricingLabel})${statusLabel}`;
};

/** Renders one side of the before/after comparison in the preview dialog. */
const BracketComparison = ({
  title,
  brackets,
}: {
  title: string;
  brackets: IPromotedListingPriceBracket[];
}) => {
  const { rows, error } = toRangedBrackets(brackets);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      {
        error
          ? <Alert severity="warning">{error}</Alert>
          : <VariableBasePriceTable rows={rows} onChange={null} readOnly={true} />
      }
    </Box>
  );
};

/**
 * Finance Control Tool – Update Promoted Listing Contract Prices.
 *
 * Promoted listing contracts are flagged as managed externally, so the subscription editor refuses
 * to open them and their variable base price brackets cannot be changed from the subscription form.
 * This tool provides the missing path: pick a client, pick one of its promoted listing contract
 * subscriptions, and edit that contract's brackets with the same `VariableBasePriceEditor` the
 * subscription form uses.
 *
 * A contract's brackets are what `promotedListingsContractCostCalculator` charges each of its child
 * listing subscriptions against, so every save is previewed as a dry run first — showing the
 * before/after brackets and the listing subscriptions that would be repriced — before it commits.
 */
export const UpdatePromotedListingContractPrices = () => {
  const [ clientId, setClientId ] = useState<Nullable<number>>(null);
  const [ selectedSubscriptionId, setSelectedSubscriptionId ] = useState<Nullable<number>>(null);
  const [ note, setNote ] = useState('');
  const [ stagedChange, setStagedChange ] = useState<Nullable<TStagedPriceChange>>(null);
  const [ preview, setPreview ] = useState<Nullable<IPromotedListingContractPriceUpdateResult>>(null);
  // Failures already reach the operator through the app-wide message alert, so this reports successes only.
  const [ successMessage, setSuccessMessage ] = useState<Nullable<string>>(null);
  /*
   * Bumped to remount the editor after a change is committed or discarded. The editor holds its own
   * draft rows and "use default values" toggle internally and only seeds them on mount, so
   * remounting is what puts them back in step with the contract's stored prices.
   */
  const [ editorGeneration, setEditorGeneration ] = useState(0);

  /*
   * Memoised, and a no-op when the picked client has not actually changed. `ClientDropdown`
   * re-invokes this from an effect whose dependencies include the handler it builds from this
   * prop, so an inline arrow would make that effect fire after every render — clearing the
   * selected contract the moment one was chosen.
   */
  const handleClientChanged = useCallback((client: Nullable<IClientDropdownItem>) => {
    const nextClientId = client?.id ?? null;

    if (nextClientId === clientId) {
      return;
    }

    setClientId(nextClientId);
    setSelectedSubscriptionId(null);
  }, [ clientId ]);

  const contracts = useAppSelector((state) => state.promotedListings.contracts);
  const contractsLoadedState = useAppSelector((state) => state.promotedListings.contractsLoadedState);
  const isLoadingContracts = contractsLoadedState === ELoadState.loading;

  useEffect(() => {
    if (!clientId) {
      clearPromotedListingContracts();
      return;
    }

    loadPromotedListingContracts(clientId);
  }, [ clientId ]);

  // A reload (including the one after a save) can retire the selected contract, so drop a stale id.
  useEffect(() => {
    setSelectedSubscriptionId((current) => (
      contracts.some((contract) => contract.subscriptionId === current) ? current : null
    ));
  }, [ contracts ]);

  // A staged edit belongs to the contract it was made on, so switching contracts abandons it.
  useEffect(() => {
    setStagedChange(null);
    setPreview(null);
  }, [ selectedSubscriptionId ]);

  const selectedContract = useMemo(
    () => contracts.find((contract) => contract.subscriptionId === selectedSubscriptionId) ?? null,
    [ contracts, selectedSubscriptionId ],
  );

  const rangedPlanPrices = useMemo(
    () => toRangedBrackets(selectedContract?.variableBasePrices ?? []),
    [ selectedContract ],
  );

  const rangedOverrides = useMemo(
    () => toRangedBrackets(selectedContract?.variableBasePriceOverrides ?? []),
    [ selectedContract ],
  );

  const vBPData = useMemo<IVBPData>(
    () => ({ rangedVBP: rangedPlanPrices.rows, rangedVBPO: rangedOverrides.rows }),
    [ rangedPlanPrices, rangedOverrides ],
  );

  /*
   * The same data the editor is showing, which is what the subscription form passes too. The
   * editor falls back to this whenever its draft is empty — on "Reset Values", and when the
   * "use default values" toggle is switched off — so it has to be the contract's *stored*
   * overrides. Seeding it from the plan's prices instead would refill the table with the very
   * values the operator just switched away from, and stage an override set that only duplicates
   * the plan.
   */
  const defaultVBPData = vBPData;

  /** What the contract bills at today, in the same shape as a staged change, so the two compare. */
  const storedChange = useMemo<TStagedPriceChange>(
    () => ({
      useDefaultPrices: rangedOverrides.rows.length === 0,
      brackets: toStagedBrackets(rangedOverrides.rows),
    }),
    [ rangedOverrides ],
  );

  /*
   * The editor reports every bracket edit, including ones that put the prices back exactly as they
   * were — most obviously flipping "use default values" on a contract that is already on plan
   * prices. Comparing against the stored prices is what keeps the review button inert until there
   * is a real change to review.
   */
  const hasStagedChange = Boolean(stagedChange) && !isEqual(stagedChange, storedChange);

  /** Closes the preview, leaving the staged edit in place so Cancel returns to it. */
  const closePreview = () => setPreview(null);

  /** Drops the staged edit and remounts the editor so it re-derives from the contract's data. */
  const resetEditor = () => {
    setPreview(null);
    setStagedChange(null);
    setEditorGeneration((generation) => generation + 1);
  };

  /**
   * Records the editor's bracket rows without sending anything. The editor calls this for every
   * change it accepts — including the "use default values" toggle, which it treats as a save — so
   * it stages the change and the operator reviews it explicitly. An empty row set means the
   * contract should fall back to its plan's prices.
   */
  const handleEditorChange = (updatedOverrides: IVariableBasePriceOverrideWithRange[]) => {
    if (!selectedContract) {
      return;
    }

    setStagedChange({
      useDefaultPrices: updatedOverrides.length === 0,
      brackets: toStagedBrackets(updatedOverrides),
    });
  };

  /** Runs the staged change as a dry run and opens the preview with what it would do. */
  const handleReviewChanges = async () => {
    if (!selectedContract || !stagedChange) {
      return;
    }

    const dryRunResult = await savePromotedListingContractPrices(selectedContract.subscriptionId, {
      ...stagedChange,
      dryRun: true,
      ...(note.trim() ? { note: note.trim() } : {}),
    });

    if (!dryRunResult) {
      return;
    }

    setPreview(dryRunResult);
  };

  /** Re-sends the previewed change with `dryRun` cleared, committing it. */
  const handleApply = async () => {
    if (!selectedContract || !stagedChange) {
      return;
    }

    const result = await savePromotedListingContractPrices(selectedContract.subscriptionId, {
      ...stagedChange,
      dryRun: false,
      ...(note.trim() ? { note: note.trim() } : {}),
    });

    resetEditor();

    if (!result) {
      return;
    }

    setNote('');
    setSuccessMessage(
      (
        result.usesDefaultPrices
          ? `Contract ${result.subscriptionId} now bills at its plan's default prices. `
          : `Contract ${result.subscriptionId} now bills at its custom price brackets. `
      )
      + `${result.affectedListingSubscriptionIds.length} listing subscription(s) repriced.`,
    );
  };

  return (
    <>
      <h3>Update Promoted Listing Contract Prices</h3>
      <p>
        Edits the variable base price brackets on a promoted listing contract subscription. These
        contracts are managed externally, so the subscription editor will not open them. The
        contract's brackets price every listing subscription underneath it.
      </p>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ClientDropdown clientId={clientId} onClientChanged={handleClientChanged} />
        </Grid>

        <Grid item xs={12}>
          {
            isLoadingContracts
              ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading promoted listing contracts...
                  </Typography>
                </Box>
              )
              : (
                <FormControl fullWidth size="small" disabled={!clientId || contracts.length === 0}>
                  <InputLabel id="promoted-listing-contract-label">Contract subscription</InputLabel>
                  <Select
                    labelId="promoted-listing-contract-label"
                    label="Contract subscription"
                    value={selectedSubscriptionId ?? ''}
                    onChange={(event) => setSelectedSubscriptionId(Number(event.target.value))}
                  >
                    {
                      contracts.map((contract) => (
                        <MenuItem key={contract.subscriptionId} value={contract.subscriptionId}>
                          {buildContractLabel(contract)}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              )
          }
        </Grid>

        {
          clientId && !isLoadingContracts && contracts.length === 0
          && <Grid item xs={12}>
            <Alert severity="info">This client has no promoted listing contract subscriptions.</Alert>
          </Grid>
        }

        {
          selectedContract
          && <Grid item xs={12}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Chip
                size="small"
                color={selectedContract.isActive ? 'success' : 'default'}
                label={selectedContract.isActive ? 'Active' : `Expired ${selectedContract.expiryDate}`}
              />
              <Chip
                size="small"
                color={selectedContract.variableBasePriceOverrides.length > 0 ? 'warning' : 'default'}
                label={
                  selectedContract.variableBasePriceOverrides.length > 0
                    ? 'Custom prices'
                    : 'Plan prices'
                }
              />
              <Chip
                size="small"
                label={`${selectedContract.listingSubscriptionIds.length} listing subscription(s)`}
              />
              <Chip size="small" label={`Base subscription ${selectedContract.baseSubscriptionId}`} />
            </Stack>
          </Grid>
        }

        {
          (rangedPlanPrices.error ?? rangedOverrides.error)
          && <Grid item xs={12}>
            <Alert severity="warning">{rangedPlanPrices.error ?? rangedOverrides.error}</Alert>
          </Grid>
        }

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Note (optional)"
            placeholder="Why is this contract priced this way?"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={!selectedContract}
          />
        </Grid>

        <Grid item xs={12}>
          <VariableBasePriceEditor
            // Remount per contract so the editor's "use default values" toggle re-derives.
            key={`${selectedContract?.subscriptionId ?? 'no-contract'}-${editorGeneration}`}
            rangedVBP={selectedContract ? vBPData.rangedVBP : EMPTY_VBP_DATA.rangedVBP}
            rangedVBPO={selectedContract ? vBPData.rangedVBPO : EMPTY_VBP_DATA.rangedVBPO}
            defaultVBPData={selectedContract ? defaultVBPData : EMPTY_VBP_DATA}
            onSave={handleEditorChange}
            disabled={!selectedContract}
            hasErrors={Boolean(rangedPlanPrices.error ?? rangedOverrides.error)}
          />
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoDisableButton
              variant="contained"
              color="primary"
              onClick={handleReviewChanges}
              disabled={!selectedContract || !hasStagedChange}
            >
              Review Changes
            </AutoDisableButton>
            {
              hasStagedChange
              && <Button color="secondary" onClick={resetEditor}>Discard Changes</Button>
            }
            <Typography variant="body2" color="text.secondary">
              {
                hasStagedChange
                  ? 'Review the change to see what it would reprice before it is applied.'
                  : 'Edit the price brackets above, then review the change.'
              }
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={Boolean(preview)} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle>Preview Price Change</DialogTitle>
        <DialogContent>
          {
            preview
            && <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This reprices {preview.affectedListingSubscriptionIds.length} listing
                subscription(s) under contract {preview.subscriptionId} from the next billing run on.
              </Alert>
              <BracketComparison title="Currently billing at" brackets={preview.previousBrackets} />
              <BracketComparison
                title={
                  preview.usesDefaultPrices
                    ? 'Will bill at (the plan\'s default prices)'
                    : 'Will bill at'
                }
                brackets={preview.newBrackets}
              />
            </>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Cancel</Button>
          <AutoDisableButton variant="contained" color="warning" onClick={handleApply}>
            Apply Price Change
          </AutoDisableButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(successMessage)} onClose={() => setSuccessMessage(null)}>
        <DialogTitle>Price Change Applied</DialogTitle>
        <DialogContent>
          <Typography color="success.main">{successMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessMessage(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
